import { Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { AlcmeonContextService } from 'src/app/core/alcmeon-context/alcmeon-context.service';
import { SitesService } from 'src/app/mobile/shared/sites.service';
import { CapabilityIdEnum } from '../../capabilities-and-permissions';
import { SiteGroupV2, SiteV2 } from '../../models';
import { BasicService } from '../../services/basic.service';
import { StrategiesService } from '../../strategies/strategies.service';
import { FeaturePageLocalStorageKeyEnum } from '../search-bar/feature-page-local-storage-key.enum';
import { SearchBarService } from '../search-bar/search-bar.service';

@Component({
  selector: 'app-filters-side-panel',
  templateUrl: './filters-side-panel.component.html',
  styleUrls: ['./filters-side-panel.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class FiltersSidePanelComponent implements OnInit {
  @Input() capabilities: CapabilityIdEnum[];
  @Input() allowSiteGroups: boolean;
  @Input() featurePageLocalStorageKey!: FeaturePageLocalStorageKeyEnum;
  @Input() mode: 'channels' | 'sites' = 'sites';
  @Input() selectionMode: 'checkbox-mode' | 'radio-button-mode' = 'checkbox-mode';
  @Input() allowedSiteTypes: number[] = [];
  @Input() siteFilterCb!: any;

  siteGroups: SiteGroupV2[] = [];
  sites: SiteV2[] = [];
  channels = [];

  siteGroupsSet = new Set();
  sitesSet = new Set();
  channelsSet = new Set();

  singleChoice = false;

  constructor(
    private searchBarService: SearchBarService,
    private sitesService: SitesService,
    private alcmeonContextService: AlcmeonContextService,
    private strategiesService: StrategiesService,
    private basicService: BasicService
  ) {}

  async ngOnInit() {
    this.singleChoice = this.selectionMode === 'radio-button-mode';

    if (this.mode === 'sites') {
      await this.initSitesMode();
    } else {
      await this.initChannelsMode();
    }
  }

  private async initSitesMode() {
    if (this.featurePageLocalStorageKey) {
      await this.searchBarService.applyFiltersFromLocaleStorageWithPrefixKey(this.featurePageLocalStorageKey);
      if (!this.searchBarService.filtersMap.has('siteIds')) {
        this.searchBarService.filtersMap.set('siteIds', new Set());
      } else {
        this.searchBarService.filtersMap.get('siteIds').forEach((id: number) => this.sitesSet.add(id));
      }
    } else {
      this.searchBarService.filtersMap.set('siteIds', new Set());
    }
    const allowedSiteIds = Object.keys(this.alcmeonContextService.self.capabilities || {}).filter(key => {
      const allowed = (this.capabilities || []).every(capabilityId =>
        this.alcmeonContextService.self.capabilities[key].includes(capabilityId)
      );
      if (!allowed) {
        return false;
      }
      if (this.allowedSiteTypes?.length) {
        const site = this.sitesService.sitesV2Map.get(+key);
        if (!site) {
          return false;
        }
        const { type } = site;
        return this.allowedSiteTypes.includes(type);
      }
      return true;
    });
    const allowed = allowedSiteIds.map(id => this.sitesService.sitesV2Map.get(+id));

    const allowedSites = [];
    const allowedSiteGroups = allowed
      .filter(site => {
        if (!this.allowSiteGroups) {
          allowedSites.push(site);
          return false;
        }

        if (!site.site_group_id) {
          allowedSites.push(site);
          return false;
        }

        return true;
      })
      .map(({ site_group_id }) => this.sitesService.siteGroupsV2Map.get(+site_group_id));

    const sortSites = (a, b) => {
      const result = (a.type_name || '').localeCompare(b.type_name || '');
      if (result === 0) {
        return (a.name || '').localeCompare(b.name || '');
      }
      return result;
    };

    allowedSiteGroups.sort(sortSites);
    allowedSites.sort(sortSites);

    if (this.allowSiteGroups) {
      const siteGroups = Array.from(new Set(allowedSiteGroups));
      if (this.siteFilterCb) {
        this.siteGroups = siteGroups.filter(this.siteFilterCb);
      } else {
        this.siteGroups = siteGroups;
      }

      if (this.featurePageLocalStorageKey) {
        await this.searchBarService.applyFiltersFromLocaleStorageWithPrefixKey(this.featurePageLocalStorageKey);
        if (!this.searchBarService.filtersMap.has('siteGroupIds')) {
          this.searchBarService.filtersMap.set('siteGroupIds', new Set());
        } else {
          this.searchBarService.filtersMap.get('siteGroupIds').forEach((id: number) => this.siteGroupsSet.add(id));
        }
      } else {
        this.searchBarService.filtersMap.set('siteGroupIds', new Set());
      }
    }
    if (this.siteFilterCb) {
      this.sites = allowedSites.filter(this.siteFilterCb);
    } else {
      this.sites = allowedSites;
    }
  }

  private async initChannelsMode() {
    if (this.featurePageLocalStorageKey) {
      await this.searchBarService.applyFiltersFromLocaleStorageWithPrefixKey(this.featurePageLocalStorageKey);
      if (!this.searchBarService.filtersMap.has('siteTypes')) {
        this.searchBarService.filtersMap.set('siteTypes', new Set());
      } else {
        this.searchBarService.filtersMap.get('siteTypes').forEach((id: string) => this.channelsSet.add(id));
      }
    } else {
      this.searchBarService.filtersMap.set('siteTypes', new Set());
    }
    const allowedSiteIds = Object.keys(this.alcmeonContextService.self.capabilities || {}).filter(key =>
      (this.capabilities || []).every(capabilityId =>
        this.alcmeonContextService.self.capabilities[key].includes(capabilityId)
      )
    );
    const allowedChannelsSet = new Set();
    allowedSiteIds.forEach(id => {
      const site = this.sitesService.sitesV2Map.get(+id);
      const strategy = this.strategiesService.strategiesMap.get(site.type);
      if (strategy) {
        strategy
          .getAllTasksTypes()
          .forEach((taskType: number) => allowedChannelsSet.add(`${strategy.getType()}|${taskType}`));
      }
    });
    this.channels = [...allowedChannelsSet].map((id: string) => {
      const [type, taskType] = id.split('|');
      const strategy = this.strategiesService.strategiesMap.get(+type);
      return {
        id,
        label: this.basicService.translate(`task/${strategy.getTaskTypeString(+taskType)}`),
        type: +type,
        taskType: +taskType,
        typeName: strategy.getTypeString(),
      };
    });
  }

  toggleAllSites({ checked }) {
    if (checked) {
      this.siteGroups.forEach(({ id }) => this.addSiteGroupId(id));
      this.sites.forEach(({ id }) => this.addSiteId(id));
    } else {
      this.siteGroups.forEach(({ id }) => this.deleteSiteGroupId(id));
      this.sites.forEach(({ id }) => this.deleteSiteId(id));
    }
    this.searchBarService.searchAndSortBS.next(true);
  }

  toggleAllChannels({ checked }) {
    if (checked) {
      this.channels.forEach(({ id }) => this.addSiteType(id));
    } else {
      this.channels.forEach(({ id }) => this.deleteSiteType(id));
    }
    this.searchBarService.searchAndSortBS.next(true);
  }

  toggleChannel(id: any, { checked }) {
    if (checked) {
      if (this.singleChoice) {
        this.channels.forEach(({ id }) => this.deleteSiteType(id));
      }
      this.addSiteType(id);
    } else {
      this.deleteSiteType(id);
    }
    this.searchBarService.searchAndSortBS.next(true);
  }

  private addSiteType(id) {
    this.channelsSet.add(id);
    this.searchBarService.filtersMap.get('siteTypes').add(id);
  }

  private deleteSiteType(id) {
    this.channelsSet.delete(id);
    this.searchBarService.filtersMap.get('siteTypes').delete(id);
  }

  toggleSiteGroup(id: number, { checked }) {
    if (checked) {
      if (this.singleChoice) {
        this.siteGroups.forEach(({ id }) => this.deleteSiteGroupId(id));
        this.sites.forEach(({ id }) => this.deleteSiteId(id));
      }
      this.addSiteGroupId(id);
    } else {
      this.deleteSiteGroupId(id);
    }
    this.searchBarService.searchAndSortBS.next(true);
  }

  toggleSite(id: number, { checked }) {
    if (checked) {
      if (this.singleChoice) {
        this.siteGroups.forEach(({ id }) => this.deleteSiteGroupId(id));
        this.sites.forEach(({ id }) => this.deleteSiteId(id));
      }
      this.addSiteId(id);
    } else {
      this.deleteSiteId(id);
    }
    this.searchBarService.searchAndSortBS.next(true);
  }

  private addSiteId(id) {
    this.sitesSet.add(id);
    this.searchBarService.filtersMap.get('siteIds').add(id);
  }

  private deleteSiteId(id) {
    this.sitesSet.delete(id);
    this.searchBarService.filtersMap.get('siteIds').delete(id);
  }

  private addSiteGroupId(id) {
    this.siteGroupsSet.add(id);
    this.searchBarService.filtersMap.get('siteGroupIds').add(id);
  }

  private deleteSiteGroupId(id) {
    this.siteGroupsSet.delete(id);
    this.searchBarService.filtersMap.get('siteGroupIds').delete(id);
  }
}
